<script lang="ts">
export default {
  name: 'TemplateThumbnail',
};
</script>

<script setup lang="ts">
import type { DashboardTemplateColumn, DashboardTemplateLayout, DashboardTemplateWidget } from '@/types/v2/DashboardTemplate';
import widgetRegistry from './widgetRegistry';

defineProps<{
  layout: DashboardTemplateLayout;
}>();

const outerColSpanClass: Record<number, string> = {
  1: 'col-span-2',
  2: 'col-span-4',
  3: 'col-span-6',
  4: 'col-span-8',
};

const colStartClass: Record<number, string> = {
  1: '',
  2: 'col-start-2',
  3: 'col-start-3',
  4: 'col-start-4',
  5: 'col-start-5',
};

function rowStartsByIndex(layout: DashboardTemplateLayout): Map<number, number> {
  const cols = sortedColumns(layout);
  const starts = new Map<number, number>();
  const maxUnits = 8;

  let rowFirstIndex = 0;
  let rowUsed = 0;

  for (let i = 0; i < cols.length; i += 1) {
    const span = Math.max(1, Math.min(cols[i].colSpan, 4)) * 2;
    if (rowUsed + span > maxUnits) {
      const start = Math.floor((maxUnits - rowUsed) / 2) + 1;
      starts.set(rowFirstIndex, start);
      rowFirstIndex = i;
      rowUsed = span;
      continue;
    }
    rowUsed += span;
  }

  if (cols.length > 0) {
    const start = Math.floor((maxUnits - rowUsed) / 2) + 1;
    starts.set(rowFirstIndex, start);
  }

  return starts;
}

const innerGridClass: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-4',
};

const widgetColorMap: Record<string, string> = {
  'programs-expiring': 'bg-warning/15 border-warning/30',
  'my-platform-clients': 'bg-info/15 border-info/30',
  'my-platform-notifications': 'bg-accent/15 border-accent/30',
  'top-clients-by-revenue': 'bg-success/15 border-success/30',
  'market-list': 'bg-secondary/15 border-secondary/30',
  'universal-search': 'bg-primary/15 border-primary/30',
  'nav-link-group': 'bg-neutral/15 border-neutral/30',
};

function sortedColumns(layout: DashboardTemplateLayout): DashboardTemplateColumn[] {
  return [...layout.columns].sort((a, b) => a.position - b.position);
}

function sortedWidgets(col: DashboardTemplateColumn): DashboardTemplateWidget[] {
  return [...col.widgets].sort((a, b) => a.position - b.position);
}

function getDisplayName(widgetType: string): string {
  return widgetRegistry[widgetType]?.displayName ?? widgetType;
}

function getWidgetColor(widgetType: string): string {
  return widgetColorMap[widgetType] ?? 'bg-base-200 border-base-300';
}

function getWidgetSpanClass(widget: DashboardTemplateWidget, col: DashboardTemplateColumn): string {
  const clamped = Math.min(widget.colSpan, col.colSpan);
  if (clamped <= 1) return '';
  return `col-span-${clamped}`;
}
</script>

<template>
  <div class="h-28 overflow-hidden rounded-md border border-base-300 bg-base-200/50 p-1.5">
    <div class="grid h-full grid-cols-8 gap-1">
      <div
        v-for="(col, idx) in sortedColumns(layout)"
        :key="col.position"
        :class="[outerColSpanClass[col.colSpan] ?? 'col-span-2', colStartClass[rowStartsByIndex(layout).get(idx) ?? 1] ?? '']"
        class="flex flex-col">
        <div
          v-if="col.widgets.length === 0"
          class="h-full rounded border border-dashed border-base-300"></div>
        <div
          v-else
          :class="innerGridClass[col.colSpan] ?? 'grid-cols-1'"
          class="grid h-full auto-rows-fr gap-0.5">
          <div
            v-for="widget in sortedWidgets(col)"
            :key="widget.position"
            :class="[getWidgetColor(widget.widgetType), getWidgetSpanClass(widget, col)]"
            class="flex items-center justify-center rounded border px-1 min-h-0">
            <span class="text-center text-[9px] leading-tight text-base-content/70">
              {{ getDisplayName(widget.widgetType) }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
